import {
  Children,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ButtonHTMLAttributes,
  ChangeEvent,
  InputHTMLAttributes,
  OptionHTMLAttributes,
  PropsWithChildren,
  SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, LockKeyhole, TriangleAlert, X } from "lucide-react";
import type { OfferSku } from "../domain/schema";
import { offerLabels } from "../data/catalog";

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "secondary" | "ghost" | "danger";
    size?: "sm" | "md" | "lg";
  }
>(function Button(
  {
    variant = "primary",
    size = "md",
    className = "",
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`button button--${variant} button--${size} ${className}`}
      {...props}
    />
  );
});

export function getInitials(value: string) {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "PK";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words.slice(0, 2).map((word) => word[0]).join("").toUpperCase();
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [onCancel, open]);

  if (!open) return null;

  return createPortal(
    <div
      className="dialog-backdrop"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <section
        className="confirm-dialog"
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header>
          <span className="confirm-dialog__icon" aria-hidden="true">
            <TriangleAlert size={20} />
          </span>
          <div>
            <h2 id={titleId}>{title}</h2>
            <p id={descriptionId}>{description}</p>
          </div>
          <button className="icon-button" type="button" aria-label="Close" onClick={onCancel}>
            <X size={18} />
          </button>
        </header>
        <footer>
          <Button ref={cancelButtonRef} variant="secondary" onClick={onCancel}>
            Keep project
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}

export function Field({
  label,
  hint,
  children,
}: PropsWithChildren<{ label: string; hint?: string }>) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

type SelectOption = {
  value: string;
  label: string;
  disabled: boolean;
};

export function AppSelect({
  children,
  className = "",
  disabled = false,
  value,
  defaultValue,
  onChange,
  id,
  name,
  required,
  autoFocus,
  ...nativeProps
}: SelectHTMLAttributes<HTMLSelectElement>) {
  const generatedId = useId();
  const listboxId = `select-listbox-${generatedId.replace(/:/g, "")}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(() =>
    String(Array.isArray(defaultValue) ? defaultValue[0] ?? "" : defaultValue ?? ""),
  );
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 260,
    side: "bottom" as "top" | "bottom",
  });

  const options = useMemo<SelectOption[]>(
    () =>
      Children.toArray(children).flatMap((child) => {
        if (!isValidElement<OptionHTMLAttributes<HTMLOptionElement>>(child) || child.type !== "option") {
          return [];
        }
        const optionValue = child.props.value ?? child.props.children ?? "";
        const label = Children.toArray(child.props.children)
          .map((part) => (typeof part === "string" || typeof part === "number" ? String(part) : ""))
          .join("");
        return [{
          value: String(optionValue),
          label: label || String(optionValue),
          disabled: Boolean(child.props.disabled),
        }];
      }),
    [children],
  );

  const controlledValue = Array.isArray(value) ? value[0] : value;
  const selectedValue = value === undefined ? internalValue : String(controlledValue ?? "");
  const selectedOption = options.find((option) => option.value === selectedValue) ?? options[0];
  const ariaLabel = nativeProps["aria-label"];
  const ariaDescribedBy = nativeProps["aria-describedby"];
  const ariaLabelledBy = nativeProps["aria-labelledby"];

  const positionMenu = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 10;
    const desiredHeight = 260;
    const availableBelow = window.innerHeight - rect.bottom - viewportPadding;
    const availableAbove = rect.top - viewportPadding;
    const openAbove = availableBelow < 180 && availableAbove > availableBelow;
    const maxHeight = Math.max(140, Math.min(desiredHeight, openAbove ? availableAbove - 8 : availableBelow - 8));
    setMenuPosition({
      top: openAbove ? rect.top - maxHeight - 6 : rect.bottom + 6,
      left: Math.max(viewportPadding, Math.min(rect.left, window.innerWidth - rect.width - viewportPadding)),
      width: rect.width,
      maxHeight,
      side: openAbove ? "top" : "bottom",
    });
  };

  const openMenu = () => {
    if (disabled || options.length === 0) return;
    const selectedIndex = options.findIndex((option) => option.value === selectedOption?.value);
    setHighlightedIndex(Math.max(0, selectedIndex));
    setOpen(true);
    window.requestAnimationFrame(positionMenu);
  };

  const chooseOption = (option: SelectOption) => {
    if (option.disabled) return;
    if (value === undefined) setInternalValue(option.value);

    if (onChange) {
      const eventTarget = document.createElement("select");
      eventTarget.name = name ?? "";
      options.forEach((item) => {
        const optionElement = document.createElement("option");
        optionElement.value = item.value;
        optionElement.textContent = item.label;
        eventTarget.append(optionElement);
      });
      eventTarget.value = option.value;
      onChange({
        target: eventTarget,
        currentTarget: eventTarget,
      } as ChangeEvent<HTMLSelectElement>);
    }

    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const handleViewportChange = () => positionMenu();
    document.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open]);

  const moveHighlight = (direction: 1 | -1) => {
    if (!options.length) return;
    let next = highlightedIndex;
    do {
      next = (next + direction + options.length) % options.length;
    } while (options[next]?.disabled && next !== highlightedIndex);
    setHighlightedIndex(next);
    window.requestAnimationFrame(() => {
      document.getElementById(`${listboxId}-option-${next}`)?.scrollIntoView({ block: "nearest" });
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        openMenu();
      } else {
        moveHighlight(event.key === "ArrowDown" ? 1 : -1);
      }
      return;
    }
    if (event.key === "Home" && open) {
      event.preventDefault();
      setHighlightedIndex(Math.max(0, options.findIndex((option) => !option.disabled)));
      return;
    }
    if (event.key === "End" && open) {
      event.preventDefault();
      let lastEnabled = options.length - 1;
      while (lastEnabled > 0 && options[lastEnabled]?.disabled) lastEnabled -= 1;
      setHighlightedIndex(Math.max(0, lastEnabled));
      return;
    }
    if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault();
      const highlighted = options[highlightedIndex];
      if (highlighted) chooseOption(highlighted);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className={`custom-select ${disabled ? "is-disabled" : ""}`}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        className={`input select custom-select__trigger ${className}`}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-haspopup="listbox"
        aria-required={required || undefined}
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open ? `${listboxId}-option-${highlightedIndex}` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleKeyDown}
      >
        <span>{selectedOption?.label || "Choose an option"}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              id={listboxId}
              role="listbox"
              className="custom-select__menu"
              data-side={menuPosition.side}
              aria-label={ariaLabel}
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
              }}
            >
              {options.map((option, index) => {
                const selected = option.value === selectedOption?.value;
                const highlighted = index === highlightedIndex;
                return (
                  <button
                    key={`${option.value}-${index}`}
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    className={`${selected ? "is-selected" : ""} ${highlighted ? "is-highlighted" : ""}`}
                    aria-selected={selected}
                    disabled={option.disabled}
                    tabIndex={-1}
                    onPointerEnter={() => setHighlightedIndex(index)}
                    onClick={() => chooseOption(option)}
                  >
                    <span>{option.label}</span>
                    {selected ? <Check size={16} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="input textarea" {...props} />;
}

export function Badge({
  children,
  tone = "neutral",
}: PropsWithChildren<{ tone?: "neutral" | "green" | "gold" | "clay" | "red" }>) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

const offerTierLabels: Partial<Record<OfferSku, string>> = {
  "paper-kingdom-unlimited": "OTO 1",
  "paper-kingdom-dfy": "OTO 2",
  "paper-kingdom-production": "OTO 3",
  "paper-kingdom-launch-growth": "OTO 4",
  "paper-kingdom-reseller": "OTO 5",
};

export function OfferTierBadge({ sku, locked = false }: { sku: OfferSku; locked?: boolean }) {
  const label = offerTierLabels[sku];
  if (!label) return null;

  return (
    <span className={`offer-tier-marker${locked ? " is-locked" : ""}`}>
      {locked ? <LockKeyhole aria-hidden="true" size={10} /> : null}
      {label}
    </span>
  );
}

export function OfferBadge({ sku, locked = false }: { sku: OfferSku; locked?: boolean }) {
  return (
    <span className="offer-badge">
      <OfferTierBadge sku={sku} locked={locked} />
      <Badge tone={sku === "paper-kingdom-fe" ? "green" : "gold"}>
        {offerLabels[sku]}
      </Badge>
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="section-heading">
      <div>
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="section-heading__actions">{actions}</div> : null}
    </div>
  );
}

export function Stat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  children,
}: PropsWithChildren<{ icon?: React.ReactNode; title: string; description: string }>) {
  return (
    <div className="empty-state">
      {icon ? <div className="empty-state__icon">{icon}</div> : null}
      <h3>{title}</h3>
      <p>{description}</p>
      {children}
    </div>
  );
}
